// Observasjonsarkivet — analytikerens flate mot historikken.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERMÅL 2026-08-17: «mulighet for å hente opp historikk for analytikerne på en
// smart måte — herunder også jobbe aktivt i innhentingsplaner osv. for å gi fra
// oss gode produkter.»
//
// Arkivet (`observasjonsarkiv.ts` + `services/hub/src/arkiv.ts`) og de fem
// endepunktene (`/arkiv`, `/arkiv/monster`, `/arkiv/dekning`,
// `/arkiv/rfi-kobling`, `/innhenting`) var ferdige og testet — men INGEN FLATE
// nådde dem. En analytiker med et komplett arkiv hun ikke kan spørre i, har
// ikke et arkiv. Dette er den siste ledningen i kjeden
// GNÅ-bilde → hub → arkiv → analytiker → etterretningsprodukt.
//
// ── FIRE TING DENNE FLATA IKKE FÅR LYVE OM ─────────────────────────────────
//  1. SØKEVINDUET. Journalen på disk er komplett, men søket treffer bare de
//     nyeste postene i minnet (`MAKS_I_MINNE = 50 000`, se `arkiv.ts`). Et tomt
//     svar betyr «ingen av de søkbare postene passer» — IKKE «ingenting skjedde».
//     Derfor står vindusstørrelsen alltid over resultatet (`arkivSokKvittering`).
//  2. TOMME DEKNINGSCELLER. «Ingen observasjon» = ingen har SETT etter. En tom
//     celle som leses som «avklart» er en aktiv løgn om hva vi vet, og hele
//     grunnen til at innhentingsplaner finnes. Forbeholdet følger tallet.
//  3. OBSERVASJONSTID vs MOTTAKSTID. Et galleribilde er tatt for timer siden; en
//     kø tømt etter sambandsbrudd leverer minuttgamle observasjoner. Forskjellen
//     er den eneste ærlige måten å si «vi visste ikke dette da beslutningen ble
//     tatt», så forsinkelsen skrives ut når den er merkbar.
//  4. RFI-KOBLING. At en observasjon BESVARER et etterretningskrav er en
//     analytikervurdering. Panelet foreslår aldri koblingen selv — det ville vært
//     å svare på eierens spørsmål med maskinens mening.
//
// Ren DOM, ingen nye avhengigheter. Tilstand og tekst er skilt ut som rene
// funksjoner (nederst mot toppen) fordi de er det som kan regnes feil.
import {
  arkivSammendrag, featureCollection, formatMgrs, latLonToMgrs, peakHoursUtc,
  type ArkivDekningscelle, type GeoFeatureCollection, type LatLon, type Observasjon,
  type ObservasjonsType, type PolResult, type Rfi,
} from '@skade/contracts';
import { trygtBildeUrl } from './shell.js';

// ── Tidsvindu ────────────────────────────────────────────────────────────────

/**
 * Forhåndsvalg for tidsvinduet. `null` = ingen nedre grense («alt vi har»).
 *
 * 48 timer er med fordi det er standardvinduet dekningsruta bruker, og 30 døgn
 * fordi mønster-i-liv trenger gjentakelser over tid for å bety noe.
 */
export const ARKIV_TIDSVINDU: [string, number | null][] = [
  ['Siste time', 3_600_000],
  ['Siste 6 timer', 6 * 3_600_000],
  ['Siste døgn', 24 * 3_600_000],
  ['Siste 48 timer', 48 * 3_600_000],
  ['Siste 7 døgn', 7 * 86_400_000],
  ['Siste 30 døgn', 30 * 86_400_000],
  ['Alt arkivet har', null],
];

export const ARKIV_TYPE_TEKST: Record<ObservasjonsType, string> = {
  bilde: 'Bilde',
  geotag: 'Geotag',
  deteksjon: 'Deteksjon',
  nod: 'Nød',
  maalestimat: 'Målestimat',
  spor: 'Spor',
  // ⚠ «ÅPEN KILDE», IKKE «HENDELSE». Etiketten er analytikerens eneste signal om
  // at dette ikke er vår egen innhenting — en politimelding er politiets
  // observasjon, gjengitt av oss. Sto det «Hendelse», kunne den blitt sitert i et
  // etterretningsprodukt som om vi hadde sett den selv.
  'apen-kilde': 'Åpen kilde',
};

/** Nærhetssøk-radius fra tråkorset. `0` = av (ikke et nærhetssøk). */
export const ARKIV_RADIUS: [string, number][] = [
  ['Av', 0],
  ['250 m', 250],
  ['1 km', 1000],
  ['5 km', 5000],
  ['20 km', 20_000],
];

/**
 * Tolk et `datetime-local`-felt til ms epoch.
 *
 * ⚠ FELTET ER LOKALTID, OG DET SKAL DET VÆRE. En analytiker som spør «hva
 * skjedde mellom 14 og 15» mener klokka på veggen sin, ikke Z. `Date.parse` av
 * en streng UTEN sone tolkes lokalt av alle nettlesere, som er nettopp det vi
 * vil — men etiketten i UI-et MÅ si «lokal tid», ellers leser hun det som Z og
 * søker i feil time (om sommeren to timer feil i Norge).
 */
export function tolkLokalTid(verdi: string): number | null {
  const s = verdi.trim();
  if (!s) return null;
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/** Søketilstanden — ren, uten DOM, fordi den er det som blir en URL. */
export class ArkivSokTilstand {
  /** Indeks i `ARKIV_TIDSVINDU`. Døgn som utgangspunkt. */
  vinduIdx = 2;
  /** Absolutt fra/til (lokal tid → ms). Satt = overstyrer `vinduIdx`. */
  fraMs: number | null = null;
  tilMs: number | null = null;
  /** Tomt sett = alle typer (ikke «ingen»). */
  typer = new Set<ObservasjonsType>();
  tekst = '';
  rapportor = '';
  rfiId = '';
  /** `null` = bryr seg ikke, `true` = bare med bilde, `false` = bare uten. */
  medBilde: boolean | null = null;
  /** Begrens til gjeldende kartutsnitt. */
  bareUtsnitt = false;
  /** Nærhetsradius fra tråkorset (m). 0 = av. */
  radiusM = 0;
  maks = 200;

  /** Vinduet søket faktisk dekker. Absolutte felt vinner over forhåndsvalget. */
  vindu(naa: number): { fraMs?: number; tilMs?: number } {
    if (this.fraMs !== null || this.tilMs !== null) {
      return {
        ...(this.fraMs !== null ? { fraMs: this.fraMs } : {}),
        ...(this.tilMs !== null ? { tilMs: this.tilMs } : {}),
      };
    }
    const spenn = ARKIV_TIDSVINDU[this.vinduIdx]?.[1] ?? null;
    return spenn === null ? {} : { fraMs: naa - spenn, tilMs: naa };
  }
}

export interface Kartboks {
  north: number;
  south: number;
  west: number;
  east: number;
}

/**
 * Søketilstand → spørrestreng mot `GET /arkiv`.
 *
 * ⚠ BARE FELT SOM ER SATT SENDES. Hubben leser `Number(null)` som 0 i to av
 * rutene sine (og 0 ER finite) — feilen er rettet der, men et tomt felt som
 * sendes som «» eller «0» ville fortsatt vært en påstand vi ikke mener. Et
 * usatt filter skal være FRAVÆRENDE, ikke null.
 */
export function arkivSporringParams(
  t: ArkivSokTilstand,
  naa: number,
  senter?: LatLon,
  boks?: Kartboks,
): string {
  const q = new URLSearchParams();
  const v = t.vindu(naa);
  if (v.fraMs !== undefined) q.set('fraMs', String(Math.round(v.fraMs)));
  if (v.tilMs !== undefined) q.set('tilMs', String(Math.round(v.tilMs)));
  if (t.typer.size) q.set('kinds', [...t.typer].join(','));
  if (t.tekst.trim()) q.set('tekst', t.tekst.trim());
  if (t.rapportor.trim()) q.set('rapportor', t.rapportor.trim());
  if (t.rfiId.trim()) q.set('rfiId', t.rfiId.trim());
  if (t.medBilde !== null) q.set('medBilde', t.medBilde ? '1' : '0');
  if (t.bareUtsnitt && boks) {
    q.set('minLat', boks.south.toFixed(6));
    q.set('maxLat', boks.north.toFixed(6));
    q.set('minLon', boks.west.toFixed(6));
    q.set('maxLon', boks.east.toFixed(6));
  }
  if (t.radiusM > 0 && senter) {
    q.set('lat', senter.lat.toFixed(6));
    q.set('lon', senter.lon.toFixed(6));
    q.set('radiusM', String(Math.round(t.radiusM)));
  }
  q.set('maks', String(Math.max(1, Math.min(500, Math.round(t.maks)))));
  return `/arkiv?${q.toString()}`;
}

// ── Lesing av svar (tolerant, men ærlig) ─────────────────────────────────────

export interface ArkivSvar {
  treff: Observasjon[];
  iMinne: number;
  lestFraDisk: number;
  ugyldigeLinjer: number;
  /** Satt når svaret ikke hadde formen vi forventet — vises, ikke svelges. */
  formfeil?: string;
}

function tall(u: unknown): number {
  return typeof u === 'number' && Number.isFinite(u) ? u : 0;
}

/**
 * Les `GET /arkiv`-svaret.
 *
 * ⚠ ET UVENTET SVAR ER IKKE ET TOMT ARKIV. Uten denne skillelinja ville en
 * endret feltnavn eller en proxy som svarer HTML vist «Ingen observasjoner» —
 * altså en påstand om verden, framfor en påstand om svaret. `formfeil` gjør
 * forskjellen synlig i panelet.
 */
export function lesArkivSvar(u: unknown): ArkivSvar {
  const o = (u ?? {}) as Record<string, unknown>;
  const raa = o['treff'];
  if (!Array.isArray(raa)) {
    return {
      treff: [], iMinne: tall(o['iMinne']), lestFraDisk: tall(o['lestFraDisk']),
      ugyldigeLinjer: tall(o['ugyldigeLinjer']),
      formfeil: 'Svaret fra hubben hadde ikke et «treff»-felt — dette er IKKE et tomt arkiv.',
    };
  }
  const treff = raa.filter(
    (x): x is Observasjon =>
      !!x && typeof x === 'object'
      && typeof (x as Observasjon).id === 'string'
      && Number.isFinite((x as Observasjon).lat)
      && Number.isFinite((x as Observasjon).lon),
  );
  const forkastet = raa.length - treff.length;
  return {
    treff,
    iMinne: tall(o['iMinne']),
    lestFraDisk: tall(o['lestFraDisk']),
    ugyldigeLinjer: tall(o['ugyldigeLinjer']),
    ...(forkastet > 0
      ? { formfeil: `${forkastet} post(er) i svaret manglet id eller posisjon og er utelatt.` }
      : {}),
  };
}

const nf = new Intl.NumberFormat('nb-NO');

/**
 * Kvitteringen som ALLTID står over resultatet.
 *
 * Første linje er sammendraget (`arkivSammendrag` fra contracts — samme setning
 * hubben regner ut, så panel og API ikke kan si ulike ting). Deretter kommer
 * søkevinduet, og ved tomt resultat det forbeholdet som hindrer at «0 treff»
 * leses som «ingenting skjedde».
 */
export function arkivSokKvittering(svar: ArkivSvar): string[] {
  const linjer = [arkivSammendrag(svar.treff)];
  linjer.push(
    `Søkevindu: ${nf.format(svar.iMinne)} poster i minnet`
    + (svar.lestFraDisk ? ` (${nf.format(svar.lestFraDisk)} lest fra disk ved oppstart)` : ''),
  );
  if (!svar.treff.length) {
    linjer.push(
      '⚠ Tomt resultat betyr at ingen av de SØKBARE postene passer — ikke at '
      + 'ingenting skjedde. Journalen på disk er komplett, men søket dekker bare '
      + 'de nyeste postene i minnet.',
    );
  }
  if (svar.ugyldigeLinjer > 0) {
    linjer.push(
      `⚠ ${nf.format(svar.ugyldigeLinjer)} linje(r) i journalen kunne ikke leses og er `
      + 'utenfor søket. Sjekk journalfila.',
    );
  }
  if (svar.formfeil) linjer.push(`⚠ ${svar.formfeil}`);
  return linjer;
}

function klokke(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16).replace('T', ' ') + 'Z';
}

/** Forsinkelse observasjon → mottak, som tekst. Tom når den er uinteressant. */
export function forsinkelseTekst(o: Observasjon): string {
  const d = o.mottattMs - o.tsMs;
  if (!Number.isFinite(d) || d < 60_000) return '';
  const min = Math.round(d / 60_000);
  if (min < 90) return `mottatt +${min} min`;
  const t = Math.round(d / 3_600_000);
  return t < 48 ? `mottatt +${t} t` : `mottatt +${Math.round(d / 86_400_000)} døgn`;
}

/** Undertekst-linja for én arkivpost. */
export function observasjonTekst(o: Observasjon): string {
  const deler = [ARKIV_TYPE_TEKST[o.kind] ?? o.kind, klokke(o.tsMs), o.kilde];
  const mgrs = latLonToMgrs({ lat: o.lat, lon: o.lon });
  if (mgrs) deler.push(formatMgrs(mgrs));
  if (o.asimutDeg !== undefined) {
    deler.push(
      `retning ${Math.round(o.asimutDeg)}°`
      + (o.sektorHalvbreddeDeg !== undefined ? ` ±${Math.round(o.sektorHalvbreddeDeg)}°` : ''),
    );
  }
  if (o.rapportor) deler.push(o.rapportor);
  const sen = forsinkelseTekst(o);
  if (sen) deler.push(sen);
  return deler.join(' · ');
}

/**
 * Kildegrunnlaget som følger observasjonen inn i et etterretningsprodukt.
 *
 * Samme rolle som `shell.onImint`-grunnlaget for et LEVENDE spor, men fra
 * arkivposten — analytikeren skal ikke skrive av panelet sitt eget innhold.
 * Mottakstida står med FORDI et produkt laget i etterkant må kunne si når
 * opplysningen faktisk var tilgjengelig.
 */
export function observasjonGrunnlag(o: Observasjon): string {
  const l = [
    `Observasjon: ${o.label}`,
    `Observert: ${new Date(o.tsMs).toISOString().slice(0, 19)}Z`,
    `Mottatt i SKADE: ${new Date(o.mottattMs).toISOString().slice(0, 19)}Z`,
    `Type: ${ARKIV_TYPE_TEKST[o.kind] ?? o.kind} · Kilde: ${o.kilde}`,
  ];
  const mgrs = latLonToMgrs({ lat: o.lat, lon: o.lon });
  if (mgrs) l.push(`Sted: ${formatMgrs(mgrs)}`);
  if (o.cepM !== undefined) l.push(`Posisjonsusikkerhet: ±${Math.round(o.cepM)} m`);
  if (o.asimutDeg !== undefined) {
    l.push(
      `Sensorretning: ${Math.round(o.asimutDeg)}° sann`
      + (o.sektorHalvbreddeDeg !== undefined ? ` ±${Math.round(o.sektorHalvbreddeDeg)}°` : ''),
    );
  }
  if (o.rekkeviddeM !== undefined) l.push(`Sektorlengde: ${Math.round(o.rekkeviddeM)} m`);
  if (o.rapportor) l.push(`Rapportert av: ${o.rapportor}`);
  if (o.rfiIder?.length) l.push(`Koblet RFI: ${o.rfiIder.join(', ')}`);
  for (const [k, v] of Object.entries(o.extra ?? {})) {
    if (k === 'Bilde') continue; // bildet følger som bilde, ikke som URL i teksten
    l.push(`${k}: ${v}`);
  }
  return l.join('\n');
}

// ── Dekningshull ─────────────────────────────────────────────────────────────

export interface DekningSvar {
  celler: ArkivDekningscelle[];
  hull: number;
  forbehold: string;
}

export function lesDekningSvar(u: unknown): DekningSvar {
  const o = (u ?? {}) as Record<string, unknown>;
  const celler = Array.isArray(o['celler'])
    ? (o['celler'] as ArkivDekningscelle[]).filter(
        (c) => !!c && typeof c === 'object' && Number.isFinite(c.senter?.lat),
      )
    : [];
  return {
    celler,
    hull: typeof o['hull'] === 'number' ? o['hull'] : celler.filter((c) => c.antall === 0).length,
    forbehold: typeof o['forbehold'] === 'string' ? o['forbehold'] : '',
  };
}

/**
 * Dekningen som tekst.
 *
 * ⚠ FORBEHOLDET ER IKKE PYNT. Hubben sender det med hvert svar, og det skal
 * VISES — ikke logges. Mangler det (gammel hub), skriver vi det selv her, fordi
 * et dekningskart uten den setningen er verre enn ingen dekningskart.
 */
export function dekningTekst(svar: DekningSvar): string[] {
  if (!svar.celler.length) return ['Ingen celler i utsnittet — zoom inn eller utvid vinduet.'];
  const dekket = svar.celler.length - svar.hull;
  return [
    `${nf.format(svar.hull)} av ${nf.format(svar.celler.length)} celler uten observasjon `
    + `(${nf.format(dekket)} dekket) i vinduet.`,
    svar.forbehold
      || 'Tom celle = KUNNSKAPSMANGEL (ingen har sett etter), ikke en negativ observasjon.',
  ];
}

/**
 * Utvalget i ord — setningen som følger med etterretningspakken.
 *
 * ⚠ DENNE ER ET ÆRLIGHETSKRAV, IKKE EN ETIKETT. Pakken bærer `bbox` og
 * `sinceMs`/`untilMs` fra KARTUTSNITTET, men arkivutvalget kan komme fra et
 * nærhetssøk, et fritekstsøk eller «alt som besvarer RFI-7». En leser i høyere
 * stab som får en fil merket bare med en boks, ville lest en avgrensning som
 * ikke fantes — og trodd at det som ikke er i fila, ikke finnes.
 *
 * ⚠ TAKET SIES HØYT. Traff søket presis `maks` poster, er utvalget nesten
 * sikkert AVKUTTET: det finnes flere, vi viste bare de nyeste. Et avkuttet
 * utvalg som ser komplett ut er den ene feilen et etterretningsprodukt ikke kan
 * bære, fordi konklusjonen «vi har ikke sett noe mer» da blir feil.
 */
export function beskrivUtvalg(
  t: ArkivSokTilstand,
  naa: number,
  antall: number,
  senter?: LatLon,
): string {
  const deler: string[] = [];
  const v = t.vindu(naa);
  if (v.fraMs === undefined && v.tilMs === undefined) {
    deler.push('hele arkivet (ingen tidsavgrensning)');
  } else {
    const navn = t.fraMs !== null || t.tilMs !== null
      ? 'egendefinert tidsrom'
      : (ARKIV_TIDSVINDU[t.vinduIdx]?.[0] ?? 'tidsvindu').toLowerCase();
    deler.push(
      `${navn} (${v.fraMs !== undefined ? klokke(v.fraMs) : 'åpen start'}`
      + ` → ${v.tilMs !== undefined ? klokke(v.tilMs) : 'åpen slutt'})`,
    );
  }
  if (t.bareUtsnitt) deler.push('avgrenset til kartutsnittet');
  if (t.radiusM > 0 && senter) {
    const mgrs = latLonToMgrs(senter);
    deler.push(
      `innenfor ${Math.round(t.radiusM)} m av `
      + (mgrs ? formatMgrs(mgrs) : `${senter.lat.toFixed(4)}, ${senter.lon.toFixed(4)}`),
    );
  }
  if (t.typer.size) {
    deler.push(`type: ${[...t.typer].map((k) => ARKIV_TYPE_TEKST[k]).join(', ')}`);
  }
  if (t.tekst.trim()) deler.push(`søkeord «${t.tekst.trim()}»`);
  if (t.rapportor.trim()) deler.push(`rapportør ${t.rapportor.trim()}`);
  if (t.rfiId.trim()) deler.push(`besvarer ${t.rfiId.trim()}`);
  if (t.medBilde === true) deler.push('bare med bilde');
  if (t.medBilde === false) deler.push('bare uten bilde');
  const tak = Math.max(1, Math.min(500, Math.round(t.maks)));
  deler.push(`${antall} observasjon(er)`);
  if (antall >= tak) {
    deler.push(
      `⚠ AVKUTTET: søket traff taket på ${tak}. Det finnes trolig flere — utvalget `
      + 'er de nyeste, ikke alle.',
    );
  }
  return `Arkivutvalg: ${deler.join(' · ')}`;
}

/**
 * Dekningscellene som GeoJSON-rutenett til kartlaget.
 *
 * ⚠ CELLENS SENTER ER FORSKJØVET EN HALV CELLE fra rutas hjørne — se
 * `arkivDekningshull`, som setter `senter = { lat: lat + celle/2 }`. Rekonstruerer
 * man ruta ved å bruke senteret som hjørne, havner hele rutenettet en halv celle
 * (~500 m) på skrå, og operatøren ville lest hullet i naboruta. Derfor ± halv
 * celle i BEGGE retninger.
 *
 * `hull` skilles ut som egen egenskap fordi kartlaget filtrerer og fargelegger på
 * den: hullene males, de dekkede cellene får bare en kant.
 */
export function dekningsrutenett(
  celler: readonly ArkivDekningscelle[],
  celleGrader: number,
): GeoFeatureCollection<{ antall: number; hull: boolean }> {
  const h = (celleGrader > 0 ? celleGrader : 0.01) / 2;
  return featureCollection(
    celler.map((c) => {
      const { lat, lon } = c.senter;
      const ring: [number, number][] = [
        [lon - h, lat - h], [lon + h, lat - h], [lon + h, lat + h],
        [lon - h, lat + h], [lon - h, lat - h],
      ];
      return {
        type: 'Feature' as const,
        geometry: { type: 'Polygon' as const, coordinates: [ring] },
        properties: { antall: c.antall, hull: c.antall === 0 },
      };
    }),
  );
}

// ── Arkivtreff som kartlag (SENS-31) ─────────────────────────────────────────

/**
 * Arkivtreffene (`ArkivPanel.siste`) → ETT kartlag, ikke bare fly-til per rad.
 *
 * ⚠ IKKE SPORLAGET. `tracks` er dagens/gårsdagens situasjonsbilde
 * (`objekt/kartobjekt.ts`); å blande gamle observasjoner inn der gjør kartet
 * ulesbart for en operatør som bare vil se NÅ. Dette er et EGET, togglbart
 * lag (`CopMap.tegnArkivtreff`/`skjulArkivtreff`) — analytikeren slår det av
 * og på selv, det havner ikke i «Objekter».
 *
 * Poster UTEN gyldig lat/lon hoppes stille over HER — i selve søkeresultatet
 * (`lesArkivSvar`/listen/bildeveggen) er de fortsatt med. Et kartlag kan ikke
 * tegne et punkt uten posisjon; en liste kan fint vise posten og la
 * analytikeren lese resten av metadataen.
 */
export function arkivtreffFeatureCollection(
  liste: readonly Observasjon[],
): GeoFeatureCollection<{ id: string; label: string }> {
  const features = liste
    .filter((o) => Number.isFinite(o.lat) && Number.isFinite(o.lon))
    .map((o) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [o.lon, o.lat] as [number, number] },
      properties: { id: o.id, label: o.label },
    }));
  return featureCollection(features);
}

// ── Mønster i liv ────────────────────────────────────────────────────────────

/**
 * Mønster-i-liv som tekst.
 *
 * ⚠ GRUNNLAGET FØRST. `patternOfLife` finner klynger i det den får; med fire
 * pings finner den «et mønster» som ikke er et mønster. Antall observasjoner
 * analysen bygger på er derfor det første som står, og under ~10 sier vi rett
 * ut at grunnlaget er for tynt.
 */
export function monsterLinjer(grunnlag: number, pol: PolResult | null): string[] {
  if (!pol || !grunnlag) return ['Ingen observasjoner i radiusen — ingenting å finne mønster i.'];
  const l = [`Grunnlag: ${nf.format(grunnlag)} observasjoner · ${pol.clusters.length} sted(er)`];
  if (grunnlag < 10) {
    l.push('⚠ For tynt grunnlag til å kalle dette et mønster. Utvid tid eller radius.');
  }
  const p = pol.primarySite;
  if (p) {
    const mgrs = latLonToMgrs(p.center);
    l.push(
      `Primærsted: ${mgrs ? formatMgrs(mgrs) : `${p.center.lat.toFixed(4)}, ${p.center.lon.toFixed(4)}`}`
      + ` · ${nf.format(p.count)} besøk · radius ${Math.round(p.radiusM)} m`,
    );
    l.push(`Første–siste: ${klokke(p.firstMs)} → ${klokke(p.lastMs)}`);
  }
  const topp = peakHoursUtc(pol);
  if (topp.length) {
    l.push(`Topptimer (UTC): ${topp.map((h) => `${String(h).padStart(2, '0')}Z`).join(', ')}`);
  }
  return l;
}

// ── Panelet ──────────────────────────────────────────────────────────────────

export interface ArkivHooks {
  /** Autentisert GET. Injisert slik at panelet er testbart uten nettverk. */
  hent: (sti: string) => Promise<unknown>;
  /** Autentisert POST med JSON-kropp. */
  send: (sti: string, kropp: unknown) => Promise<unknown>;
  naa: () => number;
  /** Tråkorset = kartsenteret (se `shell.toggleCrosshair`). */
  kartSenter: () => LatLon;
  kartBoks: () => Kartboks;
  /** Flytt kartet til en arkivpost. Panorerer bare når operatøren ber om det. */
  flyTil: (p: LatLon) => void;
  /** Observasjon → ferdig utfylt etterretningsprodukt (IMINT). */
  onImint?: (o: Observasjon) => void;
  /** Åpne RFI-er å koble mot. Tom liste = ingen kobling tilbys. */
  getRfis?: () => Rfi[];
  /** Tegn dekningsrutenettet i kartet. `celleGrader` er cellenes størrelse. */
  visDekning?: (celler: ArkivDekningscelle[], celleGrader: number) => void;
  /** Fjern dekningsrutenettet igjen. */
  skjulDekning?: () => void;
  /**
   * Tegn HELE det gjeldende søkeutvalget som ett kartlag (SENS-31) —
   * ikke sporlaget. Kalles på nytt hver gang søket endres, så laget aldri blir
   * et statisk snapshot fra da knappen ble trykket.
   */
  tegnArkivlag?: (fc: GeoFeatureCollection) => void;
  /** Fjern arkivtrefflaget igjen. */
  skjulArkivlag?: () => void;
  /**
   * Pakk utvalget som etterretningspakke. `beskrivelse` er spørringen i ord og
   * SKAL følge fila — se `beskrivUtvalg`.
   */
  pakkUtvalg?: (obs: Observasjon[], beskrivelse: string) => void;
}

const DEKNING_CELLE_GRADER = 0.01; // ~1 km i Oslofjord-breddene (hubbens default)

export class ArkivPanel {
  readonly el: HTMLElement;
  readonly tilstand = new ArkivSokTilstand();
  private apen = false;
  private kvitteringEl!: HTMLElement;
  private listeEl!: HTMLElement;
  private analyseEl!: HTMLElement;
  private veggEl!: HTMLElement;
  private visVegg = false;
  private siste: Observasjon[] = [];
  /** Sist viste dekning — brukes til å kunne skjule laget igjen. */
  private dekningPaa = false;
  /**
   * «Vis alle i kartet» (SENS-31). PÅ betyr at kartlaget speiler `siste` —
   * oppdateres i `sok()` hver gang søket endres, aldri et statisk snapshot fra
   * da knappen ble trykket.
   */
  private kartPaa = false;
  private kartKnapp!: HTMLButtonElement;

  constructor(private hooks: ArkivHooks) {
    this.el = this.bygg();
  }

  toggle() {
    this.apen = !this.apen;
    this.el.classList.toggle('open', this.apen);
    if (this.apen && !this.siste.length) void this.sok();
  }

  /** Åpne panelet med et ferdig filter — brukes av «Observasjoner» i RFI-panelet. */
  apneForRfi(rfiId: string) {
    this.tilstand.rfiId = rfiId;
    this.tilstand.vinduIdx = ARKIV_TIDSVINDU.length - 1; // alt: svaret kan være gammelt
    this.tilstand.radiusM = 0;
    this.tilstand.bareUtsnitt = false;
    this.rfiFelt.value = rfiId;
    this.vinduSel.value = String(this.tilstand.vinduIdx);
    this.utsnittBoks.checked = false;
    this.radiusSel.value = '0';
    if (!this.apen) { this.apen = true; this.el.classList.add('open'); }
    void this.sok();
  }

  private vinduSel!: HTMLSelectElement;
  private radiusSel!: HTMLSelectElement;
  private utsnittBoks!: HTMLInputElement;
  private rfiFelt!: HTMLInputElement;
  private tekstFelt!: HTMLInputElement;
  private fraFelt!: HTMLInputElement;
  private tilFelt!: HTMLInputElement;
  private bildeBoks!: HTMLInputElement;

  private bygg(): HTMLElement {
    const p = div('report-composer arkiv-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Observasjonsarkiv'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    const form = div('report-form');

    // Tidsvindu: forhåndsvalg + valgfri absolutt overstyring.
    this.vinduSel = document.createElement('select');
    this.vinduSel.className = 'checklist-sel';
    ARKIV_TIDSVINDU.forEach(([navn], i) => {
      const o = document.createElement('option');
      o.value = String(i); o.textContent = navn;
      this.vinduSel.append(o);
    });
    this.vinduSel.value = String(this.tilstand.vinduIdx);
    this.vinduSel.onchange = () => {
      this.tilstand.vinduIdx = Number(this.vinduSel.value);
      // Et forhåndsvalg skal FAKTISK gjelde: står det absolutte felt igjen, ville
      // de stille overstyrt valget operatøren nettopp gjorde.
      this.fraFelt.value = ''; this.tilFelt.value = '';
      this.tilstand.fraMs = null; this.tilstand.tilMs = null;
      void this.sok();
    };
    form.append(merket('Tidsvindu (observasjonstid)', this.vinduSel));

    this.fraFelt = document.createElement('input');
    this.fraFelt.type = 'datetime-local'; this.fraFelt.className = 'report-input';
    this.tilFelt = document.createElement('input');
    this.tilFelt.type = 'datetime-local'; this.tilFelt.className = 'report-input';
    const absRad = div('arkiv-rad');
    absRad.append(this.fraFelt, this.tilFelt);
    // «Lokal tid» står i etiketten med vilje: feltet ER lokaltid, og en analytiker
    // som tror det er Z søker i feil time (to timer feil norsk sommertid).
    form.append(merket('Fra / til (lokal tid — overstyrer vinduet)', absRad));

    // Sted: kartutsnitt og/eller nærhet til tråkorset.
    this.utsnittBoks = document.createElement('input');
    this.utsnittBoks.type = 'checkbox';
    this.utsnittBoks.onchange = () => {
      this.tilstand.bareUtsnitt = this.utsnittBoks.checked;
      void this.sok();
    };
    form.append(avkryss('Bare i gjeldende kartutsnitt', this.utsnittBoks));

    this.radiusSel = document.createElement('select');
    this.radiusSel.className = 'checklist-sel';
    for (const [navn, m] of ARKIV_RADIUS) {
      const o = document.createElement('option');
      o.value = String(m); o.textContent = navn;
      this.radiusSel.append(o);
    }
    this.radiusSel.onchange = () => {
      this.tilstand.radiusM = Number(this.radiusSel.value);
      void this.sok();
    };
    form.append(merket('Nær tråkorset (kartsenter)', this.radiusSel));

    // Typefiltre som knapperad — raskere å slå av/på enn en flervalgsliste.
    const typeRad = div('arkiv-typer');
    for (const t of Object.keys(ARKIV_TYPE_TEKST) as ObservasjonsType[]) {
      const b = document.createElement('button');
      b.className = 'arkiv-type'; b.type = 'button';
      b.textContent = ARKIV_TYPE_TEKST[t];
      b.onclick = () => {
        if (this.tilstand.typer.has(t)) this.tilstand.typer.delete(t);
        else this.tilstand.typer.add(t);
        b.classList.toggle('valgt', this.tilstand.typer.has(t));
        void this.sok();
      };
      typeRad.append(b);
    }
    form.append(merket('Type (ingen valgt = alle)', typeRad));

    this.tekstFelt = document.createElement('input');
    this.tekstFelt.className = 'report-input';
    this.tekstFelt.placeholder = 'Fritekst i etikett, kilde og metadata';
    this.tekstFelt.onchange = () => { this.tilstand.tekst = this.tekstFelt.value; void this.sok(); };
    form.append(merket('Søkeord', this.tekstFelt));

    this.rfiFelt = document.createElement('input');
    this.rfiFelt.className = 'report-input';
    this.rfiFelt.placeholder = 'RFI-id (valgfritt)';
    this.rfiFelt.onchange = () => { this.tilstand.rfiId = this.rfiFelt.value; void this.sok(); };
    form.append(merket('Besvarer RFI', this.rfiFelt));

    this.bildeBoks = document.createElement('input');
    this.bildeBoks.type = 'checkbox';
    this.bildeBoks.onchange = () => {
      this.tilstand.medBilde = this.bildeBoks.checked ? true : null;
      void this.sok();
    };
    form.append(avkryss('Bare med bilde (IMINT)', this.bildeBoks));

    const knapper = div('arkiv-rad');
    this.kartKnapp = knapp('Vis alle i kartet', () => this.byttKart());
    knapper.append(
      knapp('Søk', () => void this.sok()),
      knapp('Bildevegg', () => this.byttVegg()),
      this.kartKnapp,
      knapp('Mønster her', () => void this.monster()),
      knapp('Dekningshull', () => void this.dekning()),
      knapp('Pakk utvalget', () => this.pakk()),
    );
    form.append(knapper);
    p.append(form);

    this.kvitteringEl = div('arkiv-kvittering');
    this.analyseEl = div('arkiv-analyse');
    this.analyseEl.hidden = true;
    this.veggEl = div('arkiv-vegg');
    this.veggEl.hidden = true;
    this.listeEl = div('targetlist-list');
    p.append(this.kvitteringEl, this.analyseEl, this.veggEl, this.listeEl);
    return p;
  }

  // ── Handlinger ─────────────────────────────────────────────────────────────

  /** Kjør søket og tegn resultatet. Feil VISES; de svelges aldri til «0 treff». */
  async sok(): Promise<void> {
    this.tilstand.fraMs = tolkLokalTid(this.fraFelt.value);
    this.tilstand.tilMs = tolkLokalTid(this.tilFelt.value);
    const sti = arkivSporringParams(
      this.tilstand, this.hooks.naa(), this.hooks.kartSenter(), this.hooks.kartBoks(),
    );
    this.kvitteringEl.textContent = 'Søker …';
    try {
      const svar = lesArkivSvar(await this.hooks.hent(sti));
      this.siste = svar.treff;
      this.tegnKvittering(arkivSokKvittering(svar));
      this.tegnListe();
      this.tegnVegg();
      this.oppdaterKartlag();
    } catch (err) {
      // ⚠ EN FEILET SPØRRING ER IKKE ET TOMT ARKIV. Uten dette skillet ville et
      // 401 eller en nettverksfeil sett ut som «ingenting har skjedd» — den
      // farligste feilmeldingen et etterretningsverktøy kan gi.
      this.siste = [];
      this.tegnKvittering([`⚠ Søket feilet: ${feilTekst(err)}`,
        'Dette er en FEIL, ikke et tomt arkiv. Resultatet under er uten verdi.']);
      this.listeEl.replaceChildren();
      this.veggEl.replaceChildren();
      this.oppdaterKartlag();
    }
  }

  private byttVegg() {
    this.visVegg = !this.visVegg;
    this.tegnVegg();
  }

  /**
   * «Vis alle i kartet» / «Skjul fra kartet» (SENS-31). PÅ tegner HELE
   * `siste` som ett lag; slås den på med et tomt utvalg, tegnes et tomt lag —
   * neste `sok()` fyller det, den blir aldri et statisk snapshot.
   */
  private byttKart() {
    this.kartPaa = !this.kartPaa;
    this.kartKnapp.textContent = this.kartPaa ? 'Skjul fra kartet' : 'Vis alle i kartet';
    this.kartKnapp.classList.toggle('valgt', this.kartPaa);
    this.oppdaterKartlag();
  }

  /** Speil `siste` i kartlaget når toggelen er på — kalt fra `sok()` ved hvert søk. */
  private oppdaterKartlag() {
    if (this.kartPaa) this.hooks.tegnArkivlag?.(arkivtreffFeatureCollection(this.siste));
    else this.hooks.skjulArkivlag?.();
  }

  /** Mønster i liv rundt tråkorset. Radius følger nærhetsvalget (default 1 km). */
  private async monster(): Promise<void> {
    const c = this.hooks.kartSenter();
    const r = this.tilstand.radiusM > 0 ? this.tilstand.radiusM : 1000;
    const sti = `/arkiv/monster?lat=${c.lat.toFixed(6)}&lon=${c.lon.toFixed(6)}&radiusM=${Math.round(r)}`;
    this.analyseEl.hidden = false;
    this.analyseEl.textContent = 'Analyserer …';
    try {
      const svar = (await this.hooks.hent(sti)) as { grunnlag?: number; mønster?: PolResult };
      this.tegnAnalyse([
        `Mønster i liv innenfor ${Math.round(r)} m av tråkorset`,
        ...monsterLinjer(svar?.grunnlag ?? 0, svar?.mønster ?? null),
      ]);
    } catch (err) {
      this.tegnAnalyse([`⚠ Mønsteranalysen feilet: ${feilTekst(err)}`]);
    }
  }

  /** Dekningshull i gjeldende utsnitt — broen fra arkivet til innhentingsplanen. */
  private async dekning(): Promise<void> {
    if (this.dekningPaa) {
      this.dekningPaa = false;
      this.hooks.skjulDekning?.();
      this.analyseEl.hidden = true;
      return;
    }
    const b = this.hooks.kartBoks();
    const v = this.tilstand.vindu(this.hooks.naa());
    const q = new URLSearchParams({
      minLat: b.south.toFixed(6), maxLat: b.north.toFixed(6),
      minLon: b.west.toFixed(6), maxLon: b.east.toFixed(6),
      celleGrader: String(DEKNING_CELLE_GRADER),
    });
    if (v.fraMs !== undefined) q.set('fraMs', String(Math.round(v.fraMs)));
    if (v.tilMs !== undefined) q.set('tilMs', String(Math.round(v.tilMs)));
    this.analyseEl.hidden = false;
    this.analyseEl.textContent = 'Regner dekning …';
    try {
      const svar = lesDekningSvar(await this.hooks.hent(`/arkiv/dekning?${q.toString()}`));
      this.tegnAnalyse(['Dekning i kartutsnittet', ...dekningTekst(svar)]);
      this.hooks.visDekning?.(svar.celler, DEKNING_CELLE_GRADER);
      this.dekningPaa = svar.celler.length > 0;
    } catch (err) {
      this.tegnAnalyse([`⚠ Dekningsberegningen feilet: ${feilTekst(err)}`]);
    }
  }

  /**
   * Pakk det utvalget som ligger på skjermen til en etterretningspakke.
   *
   * ⚠ DET SOM PAKKES ER DET SOM VISES. Ikke et nytt søk, ikke «alt i boksen» —
   * nettopp de postene analytikeren ser, med spørringen skrevet ut ved siden av.
   * Ville pakken kjørt sitt eget søk, kunne fila inneholdt noe annet enn det hun
   * vurderte, og da er sporbarheten borte.
   */
  private pakk(): void {
    if (!this.hooks.pakkUtvalg) return;
    if (!this.siste.length) {
      this.tegnKvittering([
        '⚠ Ingenting å pakke — utvalget er tomt.',
        'Kjør et søk først. En pakke uten observasjoner sier ikke at området er '
        + 'tomt; den sier bare at ingen valgte noe.',
      ]);
      return;
    }
    const beskrivelse = beskrivUtvalg(
      this.tilstand, this.hooks.naa(), this.siste.length, this.hooks.kartSenter(),
    );
    this.hooks.pakkUtvalg([...this.siste], beskrivelse);
    this.tegnAnalyse(['Etterretningspakke laget', beskrivelse]);
  }

  /** Sett RFI-koblingen på en arkivpost (analytikerens vurdering, aldri automatisk). */
  private async kobleRfi(o: Observasjon, rfiIder: string[]): Promise<void> {
    try {
      const svar = (await this.hooks.send('/arkiv/rfi-kobling', { id: o.id, rfiIder })) as
        { observasjon?: Observasjon };
      const oppdatert = svar?.observasjon;
      if (oppdatert) {
        this.siste = this.siste.map((x) => (x.id === o.id ? oppdatert : x));
      } else {
        // Hubben svarte uten posten. Speil lokalt så knappen ikke ser død ut, men
        // ikke lat som vi vet mer enn vi gjør.
        this.siste = this.siste.map((x) => (x.id === o.id ? { ...x, rfiIder } : x));
      }
      this.tegnListe();
    } catch (err) {
      this.tegnKvittering([`⚠ RFI-koblingen ble IKKE lagret: ${feilTekst(err)}`]);
    }
  }

  // ── Tegning ────────────────────────────────────────────────────────────────

  private tegnKvittering(linjer: string[]) {
    this.kvitteringEl.replaceChildren(
      ...linjer.map((t, i) => span(i === 0 ? 'arkiv-sammendrag' : 'arkiv-forbehold', t)),
    );
  }

  private tegnAnalyse(linjer: string[]) {
    this.analyseEl.hidden = false;
    this.analyseEl.replaceChildren(
      ...linjer.map((t, i) => span(i === 0 ? 'arkiv-analyse-tittel' : 'arkiv-forbehold', t)),
    );
  }

  private tegnVegg() {
    const medBilde = this.siste.filter((o) => o.bildeUrl);
    this.veggEl.hidden = !this.visVegg;
    if (!this.visVegg) { this.veggEl.replaceChildren(); return; }
    if (!medBilde.length) {
      this.veggEl.replaceChildren(span('checklist-note', 'Ingen bilder i utvalget.'));
      return;
    }
    const barn: HTMLElement[] = [];
    for (const o of medBilde) {
      const url = trygtBildeUrl(o.bildeUrl ?? '');
      if (!url) continue; // en URL vi ikke tør laste vises ikke som et tomt hull
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'arkiv-thumb';
      b.title = observasjonTekst(o);
      const img = document.createElement('img');
      img.src = url; img.alt = o.label; img.loading = 'lazy';
      b.append(img);
      b.onclick = () => this.hooks.onImint?.(o);
      barn.push(b);
    }
    this.veggEl.replaceChildren(...barn);
  }

  private tegnListe() {
    if (!this.siste.length) { this.listeEl.replaceChildren(); return; }
    const rfis = this.hooks.getRfis?.() ?? [];
    const rader = this.siste.map((o) => this.rad(o, rfis));
    this.listeEl.replaceChildren(...rader);
  }

  private rad(o: Observasjon, rfis: Rfi[]): HTMLElement {
    const row = div('targetlist-row');
    const main = div('checklist-main');
    const tittel = div('checklist-item-title');
    tittel.append(span(`arkiv-merke${o.kind === 'nod' ? ' nod' : ''}`,
      ARKIV_TYPE_TEKST[o.kind] ?? o.kind));
    tittel.append(span('checklist-item-title-tekst', o.label));
    main.append(tittel, span('checklist-note', observasjonTekst(o)));

    if (o.rfiIder?.length) {
      const chips = div('arkiv-chips');
      for (const id of o.rfiIder) {
        const c = document.createElement('button');
        c.type = 'button'; c.className = 'arkiv-chip';
        const rfi = rfis.find((r) => r.id === id);
        c.textContent = `${rfi ? rfi.question.slice(0, 40) : id} ×`;
        c.title = `Fjern koblingen til ${id}`;
        c.onclick = () => void this.kobleRfi(o, (o.rfiIder ?? []).filter((x) => x !== id));
        chips.append(c);
      }
      main.append(chips);
    }

    const actions = div('targetlist-actions');
    actions.append(knapp('Kart', () => this.hooks.flyTil({ lat: o.lat, lon: o.lon })));
    if (o.bildeUrl && this.hooks.onImint) {
      actions.append(knapp('IMINT', () => this.hooks.onImint?.(o)));
    }
    // Koble til RFI: bare når det FINNES åpne RFI-er å koble mot. En tom
    // nedtrekksliste ser ut som en ødelagt knapp.
    const ukoblet = rfis.filter((r) => !(o.rfiIder ?? []).includes(r.id));
    if (ukoblet.length) {
      const sel = document.createElement('select');
      sel.className = 'checklist-sel arkiv-rfi-velg';
      const tom = document.createElement('option');
      tom.value = ''; tom.textContent = 'Koble til RFI …';
      sel.append(tom);
      for (const r of ukoblet) {
        const op = document.createElement('option');
        op.value = r.id; op.textContent = r.question.slice(0, 60);
        sel.append(op);
      }
      sel.onchange = () => {
        const id = sel.value;
        sel.value = '';
        if (id) void this.kobleRfi(o, [...(o.rfiIder ?? []), id]);
      };
      actions.append(sel);
    }
    row.append(main, actions);
    return row;
  }
}

// ── Nettverk (standardimplementasjonen main.ts sender inn) ───────────────────

/** Les feilen ut av hubbens JSON-svar når den er der; ellers statuskoden. */
async function feilAvSvar(r: Response): Promise<string> {
  try {
    const j = (await r.json()) as { error?: unknown };
    if (typeof j?.error === 'string') return `${r.status} ${j.error}`;
  } catch { /* ikke JSON — statuskoden er alt vi har */ }
  return `${r.status} ${r.statusText || 'feil'}`;
}

export async function arkivHent(sti: string): Promise<unknown> {
  const r = await fetch(sti, { credentials: 'include' });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

export async function arkivSend(sti: string, kropp: unknown): Promise<unknown> {
  const r = await fetch(sti, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(kropp),
  });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

function feilTekst(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ── Små DOM-hjelpere (samme sett som de andre panelene) ──────────────────────

function div(cls: string): HTMLElement {
  const e = document.createElement('div'); e.className = cls; return e;
}
function span(cls: string, text: string): HTMLElement {
  const e = document.createElement('span'); e.className = cls; e.textContent = text; return e;
}
function knapp(text: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'targetlist-btn'; b.textContent = text; b.onclick = run;
  return b;
}
function merket(label: string, kontroll: HTMLElement): HTMLElement {
  const wrap = div('report-field');
  wrap.append(span('report-field-label', label), kontroll);
  return wrap;
}
function avkryss(label: string, boks: HTMLInputElement): HTMLElement {
  const l = document.createElement('label');
  l.className = 'arkiv-avkryss';
  l.append(boks, span('report-field-label', label));
  return l;
}
